import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { commentsStore, rootsKey, useComment, useCommentList } from "../../hooks/useCommentsStore";
import CommentBranch from "./CommentBranch";
import CommentComposer from "./CommentComposer";
import CommentConversation from "./CommentConversation";
import { interactionErrorMessage, isBadRequest, mutationErrorMessage } from "./commentErrors";

// What the viewer may do in a comment section. Posts grant all or nothing
// (an expired post is read-only); an occurrence keeps removals open after
// it's canceled (see OccurrenceComments).
//   canCreate      new comments and replies
//   canInteract    new likes and reactions on comments
//   canEditOwn     editing one's own comments
//   canDeleteOwn   deleting one's own comments
//   canRemoveOwn   removing one's own likes and reactions
export const FULL_PERMISSIONS = Object.freeze({
  canCreate: true,
  canInteract: true,
  canEditOwn: true,
  canDeleteOwn: true,
  canRemoveOwn: true,
});

export const READ_ONLY_PERMISSIONS = Object.freeze({
  canCreate: false,
  canInteract: false,
  canEditOwn: false,
  canDeleteOwn: false,
  canRemoveOwn: false,
});

// Comments for one target (a post or an occurrence; see useCommentsStore):
// either the paginated root list, or — when `focusedCommentId` is set — a
// focused conversation. Owns the composer target and the single error line;
// all comment data lives in the shared store.
//
// `disabledMessage` replaces the composer's placeholder while nothing can be
// posted. `errorMessages` adapts the shared error wording to the target, and
// `onRequestError(err)` is told about every failed request (an occurrence uses
// it to re-check its access).
export default function CommentsSection({
  target,
  permissions,
  disabledMessage: readOnlyMessage = null,
  errorMessages = { interaction: interactionErrorMessage, mutation: mutationErrorMessage },
  focusedCommentId,
  onOpenConversation,
  onCloseConversation,
  onCommentCountChange,
  onRequestError,
}) {
  const { token, user } = useContext(AuthContext);
  // Reset the cache if a different account signed in during this tab session.
  commentsStore.ensureSession(token);

  const currentUserId = user?.pk ?? user?.id ?? null;
  const [composerTarget, setComposerTarget] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const inputRef = useRef(null);
  const deletingRef = useRef(new Set());

  // Latest parent callbacks and messages, so `actions` can stay stable across renders.
  const callbacksRef = useRef();
  callbacksRef.current = { onOpenConversation, onCommentCountChange, onRequestError, errorMessages };

  const reportError = (err) => callbacksRef.current.onRequestError?.(err);

  // A new conversation (or returning to all comments) starts with a clean composer.
  useEffect(() => {
    setComposerTarget(null);
    setErrorMessage(null);
  }, [focusedCommentId]);

  // Losing the right to post (an expiring post, a canceled occurrence) drops
  // any reply/edit in progress.
  useEffect(() => {
    if (!permissions.canCreate) setComposerTarget(null);
  }, [permissions.canCreate]);

  // Each time a target is opened, refetch its first page of root comments (any
  // cached comments stay on screen meanwhile). Returning from a conversation
  // within the same visit reuses the cache instead.
  const refreshedTargetRef = useRef(null);
  const targetId = `${target.type}:${target.id}`;
  useEffect(() => {
    if (focusedCommentId) return;
    if (refreshedTargetRef.current !== targetId) {
      refreshedTargetRef.current = targetId;
      commentsStore.refreshTarget(target);
    } else {
      commentsStore.loadFirstPageIfNeeded(rootsKey(target));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, focusedCommentId]);

  const focusInput = () => setTimeout(() => inputRef.current?.focus(), 0);

  const actions = useMemo(() => {
    const handleInteractionError = (err, comment, action) => {
      setErrorMessage(callbacksRef.current.errorMessages.interaction(err, action));
      reportError(err);
      // Pick up a deletion (or expiry) that happened while the page was open.
      if (isBadRequest(err)) commentsStore.reloadComment(target, comment.id);
    };

    return {
      target,
      currentUserId,
      ...permissions,
      onReply: (comment) => {
        if (comment.is_deleted) return;
        setErrorMessage(null);
        setComposerTarget({ kind: "reply", comment });
        focusInput();
      },
      onEdit: (comment) => {
        if (comment.is_deleted) return;
        setErrorMessage(null);
        setComposerTarget({ kind: "edit", comment });
        focusInput();
      },
      onDelete: async (comment) => {
        if (deletingRef.current.has(comment.id)) return;
        if (!window.confirm("Delete this comment? Replies to it will stay visible.")) return;
        deletingRef.current.add(comment.id);
        setErrorMessage(null);
        try {
          const count = await commentsStore.deleteComment(target, comment);
          if (count != null) callbacksRef.current.onCommentCountChange(count);
          // Don't leave the composer pointed at a comment that no longer accepts replies/edits.
          setComposerTarget((current) => (current?.comment.id === comment.id ? null : current));
        } catch (err) {
          console.error("Failed to delete comment", err);
          setErrorMessage(callbacksRef.current.errorMessages.mutation(err, "delete"));
          reportError(err);
        } finally {
          deletingRef.current.delete(comment.id);
        }
      },
      onToggleLike: (comment) => {
        setErrorMessage(null);
        commentsStore
          .toggleLike(target, comment.id)
          .catch((err) => handleInteractionError(err, comment, "update your like"));
      },
      onReact: (comment, reactionType) => {
        setErrorMessage(null);
        commentsStore
          .react(target, comment.id, reactionType)
          .catch((err) => handleInteractionError(err, comment, "update your reaction"));
      },
      onOpenConversation: (commentId) => callbacksRef.current.onOpenConversation(commentId),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, currentUserId, permissions]);

  const focus = useComment(target, focusedCommentId);

  const handleSubmit = async (text) => {
    const submitted = composerTarget;
    setErrorMessage(null);
    try {
      if (submitted?.kind === "edit") {
        await commentsStore.editComment(target, submitted.comment, text);
      } else {
        // No explicit target: a root comment, or a reply to the focused
        // comment in a conversation.
        const parentId =
          submitted?.kind === "reply" ? submitted.comment.id : focusedCommentId ?? null;
        const count = await commentsStore.createComment(target, text, parentId);
        if (count != null) onCommentCountChange(count);
      }
      setComposerTarget((current) => (current === submitted ? null : current));
      return true;
    } catch (err) {
      console.error("Failed to submit comment", err);
      setErrorMessage(errorMessages.mutation(err, submitted?.kind === "edit" ? "edit" : "create"));
      reportError(err);
      const affected = submitted?.comment ?? focus;
      if (isBadRequest(err) && affected) commentsStore.reloadComment(target, affected.id);
      return false;
    }
  };

  let disabledMessage = null;
  if (!permissions.canCreate) disabledMessage = readOnlyMessage;
  else if (focusedCommentId && focus?.is_deleted && !composerTarget)
    disabledMessage = "Choose Reply on a comment to respond.";

  const defaultContextLabel =
    focusedCommentId && focus && !focus.is_deleted ? `Replying to @${focus.user}` : null;

  return (
    <>
      {focusedCommentId ? (
        <CommentConversation
          target={target}
          commentId={focusedCommentId}
          actions={actions}
          onBack={onCloseConversation}
        />
      ) : (
        <RootComments target={target} actions={actions} />
      )}

      {/* COMMENT FORM */}
      <div className="pb-20 sm:pb-1 sticky bottom-0 bg-white pt-2">
        {errorMessage && (
          <p className="text-xs text-red-600 mb-1" role="alert">
            {errorMessage}
          </p>
        )}
        <CommentComposer
          inputRef={inputRef}
          target={composerTarget}
          placeholder={focusedCommentId ? "Add a reply…" : "Write a Comment..."}
          defaultContextLabel={defaultContextLabel}
          disabledMessage={disabledMessage}
          onCancelTarget={() => setComposerTarget(null)}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
}

// A target's root comments, newest first, with infinite scroll plus a
// "Load more" button fallback.
function RootComments({ target, actions }) {
  const key = rootsKey(target);
  const list = useCommentList(key);

  const loadMore = useCallback(() => {
    if (list.loaded && list.next && !list.loading && !list.error) {
      commentsStore.loadNextPage(key);
    }
  }, [list, key]);
  const sentinelRef = useInfiniteScroll(loadMore);

  return (
    <section aria-label="Comments" className="mb-4">
      {list.loaded && list.ids.length === 0 && !list.loading && (
        <p className="text-sm text-gray-500">No comments yet...</p>
      )}
      {!list.loaded && list.loading && (
        <p className="text-sm text-gray-400" role="status">
          Loading comments…
        </p>
      )}

      {list.ids.map((id) => (
        <CommentBranch key={id} commentId={id} level={1} actions={actions} />
      ))}

      {list.error ? (
        <button
          type="button"
          onClick={() =>
            list.loaded ? commentsStore.loadNextPage(key) : commentsStore.refreshTarget(target)
          }
          className="py-1 text-sm text-blue-500 hover:text-blue-600"
        >
          {list.loaded ? "Couldn't load more comments · Retry" : "Couldn't load comments · Retry"}
        </button>
      ) : (
        list.loaded &&
        list.next && (
          <button
            type="button"
            onClick={loadMore}
            disabled={list.loading}
            className="py-1 text-sm text-gray-500 hover:text-gray-800 disabled:text-gray-400"
          >
            {list.loading ? "Loading…" : "Load more comments"}
          </button>
        )
      )}
      <div ref={sentinelRef} className="h-6" />
    </section>
  );
}
